import dayjs from 'dayjs';
import gqlClient from '@discover-data/gql/client';
import { DATE_FORMAT_DAY_JS } from '@discover-lib/dateFormats';
import isNonNull from '@discover-lib/isNonNull';
import FilteredRestaurantsCarouselQuery, {
  type QueryVariables,
} from '@discover-data/gql/queries/filteredRestaurantsCarousel';

export type FilteredRestaurantsCarouselMode = 'newArrivals' | 'mostPopular' | 'availableToday';

const MIN_NEW_ARRIVALS = 3;

function getQueryVariables(
  mode: FilteredRestaurantsCarouselMode,
  availableTodayDate?: string
): Omit<QueryVariables, 'regionId'> {
  switch (mode) {
    case 'mostPopular':
      return { sort: 'RANKING' };
    case 'availableToday':
      return {
        sort: 'FEATURED_NEW_TOP',
        sessions: ['DINNERANY'],
        dates: [availableTodayDate ?? dayjs().format(DATE_FORMAT_DAY_JS)],
      };
    case 'newArrivals':
    default:
      // Unfiltered so older restaurants are available to backfill regions with < 3 new arrivals.
      return { sort: 'NEWEST' };
  }
}

function withNewArrivalsBackfill<Restaurant extends { isNew?: boolean | null }>(
  restaurants: Restaurant[]
) {
  const newArrivals = restaurants.filter(({ isNew }) => isNew);

  if (newArrivals.length >= MIN_NEW_ARRIVALS) {
    return newArrivals;
  }

  const backfill = restaurants.filter(({ isNew }) => !isNew);

  return [...newArrivals, ...backfill.slice(0, MIN_NEW_ARRIVALS - newArrivals.length)];
}

export type GetFilteredRestaurantsCarouselParams = {
  regionId: number;
  mode: FilteredRestaurantsCarouselMode;
  abortSignal?: AbortSignal;
  /** City-local today for `availableToday`. Omitted callers keep the browser-local date. */
  availableTodayDate?: string;
};

export default async function getFilteredRestaurantsCarousel({
  regionId,
  mode,
  abortSignal,
  availableTodayDate,
}: GetFilteredRestaurantsCarouselParams) {
  const { allRestaurants } = await gqlClient({
    query: FilteredRestaurantsCarouselQuery,
    variables: {
      regionId,
      ...getQueryVariables(mode, availableTodayDate),
    },
    abortSignal,
  });

  const restaurants = (allRestaurants || []).filter((restaurant) => isNonNull(restaurant, ['id']));

  return mode === 'newArrivals' ? withNewArrivalsBackfill(restaurants) : restaurants;
}

export type FilteredRestaurant = Awaited<ReturnType<typeof getFilteredRestaurantsCarousel>>[number];
